import { useMemo } from 'react';
import styled from 'styled-components';

import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { Metrics } from 'components/insights/constants';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import DataTable from 'components/shared/DataTable';
import { useReferralsInsightsStore } from 'stores/referralsInsightsStore';
import { colors } from 'styles/theme/colors';

import {
  formatDaysValue,
  formatPercentage,
  formatResponseTime,
  getEntityColumnHeader,
  getReferralCountLabel,
  validMetricValue,
  VIEW_BY_DIMENSION_MAP,
} from '../constants';
import { useReferralsInsightsContext } from '../ReferralsContainer';

type ReferralTableRow = {
  entityName: string;
  entityId: string;
  referrals: number;
  uniquePatients: number;
  acceptedRate: number | null;
  deniedRate: number | null;
  canceledRate: number | null;
  pendingRate: number | null;
  avgResponseTime: number | null;
  avgCreatedToDischarge: number | null;
};

const CELL_PADDING = { meta: { padding: '16px 12px' } };

type Props = {
  loading: boolean;
  rollups: AnalyticsResponseRollupCollection;
};

export default function ReferralDetailsTable({ loading, rollups }: Props) {
  const { sendingGroupType } = useReferralsInsightsContext();
  const viewBy = useReferralsInsightsStore((state) => state.viewBy);
  const selectedReceivingGroupType = useReferralsInsightsStore((state) => state.selectedReceivingGroupType);
  const sendingGroupTypeName = sendingGroupType?.displayName ?? '';
  const receivingGroupTypeName = selectedReceivingGroupType?.displayName ?? '';
  const dimension = VIEW_BY_DIMENSION_MAP[viewBy];

  const tableData = useMemo<ReferralTableRow[]>(() => {
    // Per-entity sub-totals from rollups (grouping 1: entity present, status rolled up)
    const entityRollup = rollups.groupings[1];
    if (!entityRollup) return [];

    const entityNames = entityRollup.dimensions.getCurrentDimensionValuesByIndex(0);
    const entityIds = entityRollup.dimensions.getCurrentDimensionValuesByIndex(1);
    const referralCounts = entityRollup.metrics.getCurrentSeries(Metrics.REVIEW_ID_COUNT);
    const uniquePatientCounts = entityRollup.metrics.getCurrentSeries(Metrics.LOCATION_EPISODE_ID_COUNT);
    const acceptanceRates = entityRollup.metrics.getCurrentSeries(Metrics.ACCEPTANCE_RATE);
    const denialRates = entityRollup.metrics.getCurrentSeries(Metrics.DENIAL_RATE);
    const cancelRates = entityRollup.metrics.getCurrentSeries(Metrics.CANCEL_RATE);
    const pendingRates = entityRollup.metrics.getCurrentSeries(Metrics.PENDING_RATE);
    const responseTimes = entityRollup.metrics.getCurrentSeries(Metrics.RESPONSE_TIME_SECONDS_AVERAGE);
    const createdToDischargeDays = entityRollup.metrics.getCurrentSeries(Metrics.CREATED_TO_DISCHARGE_DAYS_AVERAGE);

    return entityNames.map((entityName, i) => ({
      entityName,
      entityId: entityIds[i] ?? '',
      referrals: referralCounts[i] ?? 0,
      uniquePatients: uniquePatientCounts[i] ?? 0,
      acceptedRate: validMetricValue(acceptanceRates[i]),
      deniedRate: validMetricValue(denialRates[i]),
      canceledRate: validMetricValue(cancelRates[i]),
      pendingRate: validMetricValue(pendingRates[i]),
      avgResponseTime: validMetricValue(responseTimes[i]),
      avgCreatedToDischarge: validMetricValue(createdToDischargeDays[i]),
    }));
  }, [rollups]);

  const columnHelper = createColumnHelper<ReferralTableRow>();

  const columns = useMemo<ColumnDef<ReferralTableRow, any>[]>(
    () => [
      columnHelper.accessor('entityName', {
        header: getEntityColumnHeader(dimension, { sendingGroupTypeName, receivingGroupTypeName }),
        // TODO: OLIO-2214 — Summary detail slide out view
        cell: (info) => <EntityLink>{info.getValue()}</EntityLink>,
        size: 180,
        enableSorting: true,
        meta: { padding: '16px 12px 16px 24px' },
      }),
      columnHelper.accessor('referrals', {
        header: getReferralCountLabel(dimension),
        cell: (info) => info.getValue(),
        size: 140,
        enableSorting: true,
        meta: { padding: '16px 12px 16px 24px' },
      }),
      columnHelper.accessor('uniquePatients', {
        header: 'Unique Patients',
        cell: (info) => info.getValue(),
        size: 130,
        enableSorting: true,
        ...CELL_PADDING,
      }),
      columnHelper.accessor('acceptedRate', {
        header: 'Acceptance Rate (%)',
        cell: (info) => <GreenText>{formatPercentage(info.getValue())}</GreenText>,
        size: 155,
        enableSorting: true,
        ...CELL_PADDING,
      }),
      columnHelper.accessor('deniedRate', {
        header: 'Denial Rate (%)',
        cell: (info) => <RedText>{formatPercentage(info.getValue())}</RedText>,
        size: 130,
        enableSorting: true,
        ...CELL_PADDING,
      }),
      columnHelper.accessor('canceledRate', {
        header: 'Cancel Rate (%)',
        cell: (info) => formatPercentage(info.getValue()),
        size: 130,
        enableSorting: true,
        ...CELL_PADDING,
      }),
      columnHelper.accessor('pendingRate', {
        header: 'Pending Rate (%)',
        cell: (info) => formatPercentage(info.getValue()),
        size: 135,
        enableSorting: true,
        ...CELL_PADDING,
      }),
      columnHelper.accessor('avgResponseTime', {
        header: 'Avg. Response Time',
        cell: (info) => formatResponseTime(info.getValue()),
        size: 155,
        enableSorting: true,
        ...CELL_PADDING,
      }),
      columnHelper.accessor('avgCreatedToDischarge', {
        header: 'Avg. Referral Created to Discharge',
        cell: (info) => formatDaysValue(info.getValue()),
        size: 235,
        enableSorting: true,
        ...CELL_PADDING,
      }),
    ],
    [columnHelper, dimension, sendingGroupTypeName, receivingGroupTypeName]
  );

  const defaultSort = [
    {
      id: 'referrals',
      desc: true,
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={tableData}
      defaultSortBy={defaultSort}
      loading={loading}
      hasFooter={false}
      maxHeight='none'
    />
  );
}

const EntityLink = styled.span`
  color: ${({ theme }) => theme.colors.primaryBlue};
  font-weight: var(--font-weight-bold);
`;

const GreenText = styled.span`
  color: ${({ theme }) => theme.colors.accentGreen};
  background-color: ${colors.accentGreen05};
  font-weight: var(--font-weight-bold);
  display: flex;
  align-items: center;
  align-self: stretch;
  margin: -16px -12px;
  padding: 16px 12px;
  flex: 1;
`;

const RedText = styled.span`
  color: ${({ theme }) => theme.colors.accentRed};
  background-color: ${colors.accentRed05};
  font-weight: var(--font-weight-bold);
  display: flex;
  align-items: center;
  align-self: stretch;
  margin: -16px -12px;
  padding: 16px 12px;
  flex: 1;
  width: 100%;
`;
